import HowBillingWorksCard from "@/app/(general-header)/subscription-checkout/HowBillingWorksCard";
import TrustlyInfoCard from "@/app/(general-header)/subscription-checkout/TrustlyInfoCard";
import {
  formatSubscriptionPrice,
  formatTrustlyBonusDays,
  getTrustlyBillingDates,
} from "@/app/(general-header)/subscription-checkout/trustly-bonus-utils";

interface TrustlyBonusSectionProps {
  isTrustlyBonusEligible: boolean;
  trustlyBonusDays?: number;
  unitAmount: number;
  interval: string;
  passName?: string;
}

export default function TrustlyBonusSection({
  isTrustlyBonusEligible,
  trustlyBonusDays,
  unitAmount,
  interval,
  passName = "Pass",
}: TrustlyBonusSectionProps) {
  const bonusDays = trustlyBonusDays ?? 0;
  const billingDates = getTrustlyBillingDates(bonusDays, interval);
  const formattedPrice = formatSubscriptionPrice(unitAmount, interval);
  const passLabel = `${passName} membership`;

  if (!isTrustlyBonusEligible) {
    return (
      <div className="w-full mb-4 lg:mb-6">
        <HowBillingWorksCard
          billingDates={billingDates}
          formattedPrice={formattedPrice}
          showMembershipBadge
          fullWidth
        />
      </div>
    );
  }

  const hasBonusDays = bonusDays > 0;

  return (
    <div className="flex flex-col gap-4 lg:gap-6 w-full mb-4 lg:mb-6">
      <div className="flex flex-col gap-2 lg:gap-3 w-full">
        <div className="flex items-center gap-[5px] lg:gap-2 w-fit max-w-full bg-gradient-to-r from-[#ffd27f] to-[#ffa135] rounded-full px-3 py-[5px] lg:py-1.5">
          <img
            alt=""
            aria-hidden="true"
            className="size-5 shrink-0"
            height={20}
            src="/assets/images/subscription-checkout/exclusive-bonus-gift.png"
            width={20}
          />
          <span className="text-[10px] lg:text-xs leading-3 lg:leading-4 font-semibold text-black uppercase">
            Exclusive bonus available
          </span>
        </div>
        <div className="min-w-0 text-[17px] lg:text-[22px] leading-6 lg:leading-7 font-bold text-black break-words">
          {hasBonusDays ? (
            <>
              Complete the subscription now and get
              <br />
              <span className="text-primary lg:hidden">{formatTrustlyBonusDays(bonusDays)} BONUS</span>
              <span className="hidden lg:inline">
                <span className="text-primary">{formatTrustlyBonusDays(bonusDays)}</span>{" "}
                <span className="text-primary">BONUS</span>
              </span>{" "}
              of {passLabel}
            </>
          ) : (
            <>Complete the subscription now and get an exclusive BONUS of {passLabel}</>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:gap-4 w-full max-w-[313px] lg:max-w-[432px]">
        <TrustlyInfoCard title="Your bonus" bodyClassName="flex-1">
          <div className="flex flex-col gap-1 w-full">
            <div className="min-w-0 text-sm lg:text-base leading-5 lg:leading-[22px] font-medium text-dark-1 break-words">
              {hasBonusDays ? `Full Pass access for ${bonusDays} days, on us` : "Full Pass access on us"}
            </div>
            <div className="min-w-0 text-xs lg:text-[13px] leading-4 lg:leading-[18px] font-normal text-gray-1 break-words">
              Your Pass activates immediately. Your paid subscription starts{" "}
              {billingDates.paidSubscriptionStartFormatted}.
            </div>
          </div>
        </TrustlyInfoCard>

        <HowBillingWorksCard billingDates={billingDates} formattedPrice={formattedPrice} showMembershipBadge={false} />
      </div>
    </div>
  );
}
